import { useEffect, useLayoutEffect, useRef, useState, type Dispatch } from 'react';
import { useKeyedValue } from '../../shared/use_keyed_value.js';
import {
  priceBounds,
  serverCuts,
  serverOrder,
  type FlightServerCuts,
  type FlightServerOrder,
} from '../results/filter_logic.js';
import {
  activeOffers,
  explorerRefreshing,
  type ExplorerEvent,
  type ExplorerState,
} from './explorer_state.js';
import {
  EXPLORER_PAGE_LIMIT,
  type FlightExplorerOffer,
  type FlightPageInfo,
  type FlightSearchFacets,
  type FlightSearchFn,
} from './search_source.js';

/**
 * A âncora do refine: uma ref opaca de oferta que identifica a sessão de busca — e a
 * ÚLTIMA que existiu NAQUELA busca serve, não só a que está em cena. Um corte que
 * zera a página zerava a âncora junto, e aí o próximo refine não tinha por onde
 * chamar: a pessoa ficava presa na lista vazia sem conseguir afrouxar o corte.
 *
 * Trocou a busca (`search`), a âncora morre com ela: um ref da sessão anterior não é
 * handle da nova, e refinar por ele filtraria uma lista que ninguém está vendo.
 */
function useSearchAnchor(offers: FlightExplorerOffer[], search: string): string | undefined {
  const [last, setLast] = useState<{ search: string; offerId: string } | undefined>(undefined);
  const inScene = offers[0]?.offerId;
  if (inScene && (last?.search !== search || last.offerId !== inScene)) {
    setLast({ search, offerId: inScene });
  }
  const kept = last?.search === search ? last.offerId : undefined;
  return inScene ?? kept;
}

/**
 * A faixa de preço da BUSCA, acumulada pela mais larga já vista nesta busca.
 *
 * O provider é honesto e é justamente por isso que a faceta não serve crua: quando o
 * corte É de preço, a faixa que ele devolve é a do conjunto CORTADO (medido no
 * fixture: `maxPrice: 700` volta com `priceRange.max <= 700`), e um corte que zera a
 * página não devolve faixa nenhuma. Guardar isso como teto do slider fazia o trilho
 * encolher até onde o dedo soltou — catraca de mão única — e sumir de vez no corte
 * vazio. Nos outros eixos a faixa já é a do conjunto (`stops: NONSTOP` derruba o
 * total e não mexe no `priceRange`), então alargar nunca mente: só desfaz o
 * estreitamento que o próprio corte de preço causou.
 *
 * Busca nova zera a memória — a faixa é DA busca, e a comparação por `search` é a
 * mesma que governa as facetas.
 */
function widenPriceRange(
  memo: { current: { search: string; range?: { min: number; max: number } } },
  search: string,
  facets: FlightSearchFacets,
): { priceRange?: { min: number; max: number }; learned: boolean } {
  if (memo.current.search !== search) memo.current = { search };
  const known = memo.current.range;
  const next = facets.priceRange;
  if (next) {
    memo.current.range = known
      ? { min: Math.min(known.min, next.min), max: Math.max(known.max, next.max) }
      : next;
  }
  return {
    ...(memo.current.range ? { priceRange: memo.current.range } : {}),
    // Primeira vez que esta busca revela a faixa do conjunto — o único momento em
    // que rebasear o teto não pode estar pisando numa escolha da pessoa.
    learned: known === undefined && memo.current.range !== undefined,
  };
}

/** Página que o scroll pediu, amarrada à busca E ao corte em que foi pedida
 *  (ver `useServerFilter`). */
interface WantedPage {
  search: string;
  key: string;
  number: number;
  tick: number;
}

/** O número de página + `tick` que o efeito deve pedir agora. O `wantedPage` só
 *  vale se busca E corte baterem com o corrente — mudou qualquer um dos dois, o
 *  pedido de página morre sozinho e deriva pra página 1 (`tick: 0`, que nunca
 *  bate com um tick de retry real). `tick` existe pro retry da MESMA página
 *  re-disparar o efeito (o `number` sozinho não muda nas deps). Devolve sempre
 *  um valor definido (nunca undefined) para o topo de `useServerFilter` nunca
 *  precisar de ternário nem optional chaining nas deps — ele já mora no teto de
 *  complexidade (mesma razão de `cabinSeam`/`widenPriceRange`). */
function pageWantedFor(
  wantedPage: WantedPage | undefined,
  search: string,
  key: string,
): { number: number; tick: number } {
  const matches = wantedPage && wantedPage.search === search && wantedPage.key === key;
  return matches ? { number: wantedPage.number, tick: wantedPage.tick } : { number: 1, tick: 0 };
}

/**
 * Rodada de refine em voo, por SEQ e não por boolean. Com boolean a flag ficava
 * acesa pra sempre quando a rodada perdia a posse antes de responder — busca nova
 * que volta VAZIA, busca que FALHA ou explorer congelado saem do efeito sem
 * agendar nada, e o `false` do callback nunca chegava. A coluna ficava a 60% e com
 * `aria-busy` mentindo até recarregar.
 *
 * Sem refine possível (sem seam, sem âncora ou congelado) não há rodada nova pra
 * acender: a de antes se apaga no render (o timer que acenderia uma rodada só existe
 * com ele ligado).
 */
function useBusySeq(seam: boolean, hasAnchor: boolean, frozen: boolean) {
  const [busySeq, setBusySeq] = useState(0);
  if ((!seam || !hasAnchor || frozen) && busySeq !== 0) setBusySeq(0);
  return [busySeq, setBusySeq] as const;
}

/** A página CORRENTE da perna, só quando `ready` — o narrowing sem cast que o
 *  scroll precisa pra saber se há próxima. Fora do componente pelo mesmo
 *  motivo de `pageWantedFor`. */
function legPageOf(leg: ExplorerState['legs'][number] | undefined): FlightPageInfo | undefined {
  return leg?.phase === 'ready' ? leg.page : undefined;
}

/**
 * O filtro que RODA NO SERVIDOR: a cada mudança de eixo, uma busca filtrada no
 * mesmo `searchKey` (200–800ms) que devolve a página e as facetas do conjunto.
 * Sem isso o painel cortava e contava sobre a primeira página da busca — 30 de
 * 127 voos numa rota doméstica, 30 de 544 numa internacional —, então "Direto 15"
 * era o número da página, não do inventário.
 *
 * Debounce de 250ms porque arrastar o slider de preço emite um evento por pixel.
 * O corte do cliente (`state.filter`) continua aplicado sobre a página: é ele que
 * cobre os dois eixos que o provider não filtra (chegada e aeroportos).
 *
 * Lê o estado inteiro (em vez de receber eixo, perna e congelamento soltos) porque
 * cada uma dessas contas tem uma regra que é DESTE assunto — qual perna ancora, de
 * que busca são as facetas, que fase não refina — e no corpo do explorer elas ficavam
 * longe da explicação.
 */
export function useServerFilter(
  fs: FlightSearchFn | undefined,
  state: ExplorerState,
  send: Dispatch<ExplorerEvent>,
  readOnly: boolean | undefined,
) {
  const { filter } = state;
  const leg = state.activeLeg;
  const offers = activeOffers(state);
  /**
   * Fase que o refine NÃO cobre: a volta do round-trip. Lá as ofertas em cena são
   * PARES, e o refine do servidor ancora na ida (é ela que carrega o searchKey da
   * busca) — filtrar ali re-buscaria e regravaria a IDA debaixo da lista de voltas.
   * Congelado, o painel volta a cortar e contar no cliente, como antes.
   */
  const frozen =
    Boolean(readOnly) ||
    state.confirmed ||
    (state.request.mode === 'round_trip' && leg === 1) ||
    // Semente com a busca viva em voo: refinar aqui gastaria um request na sessão
    // VELHA (a âncora é uma oferta da semente) para produzir uma página que o pouso
    // da busca substitui em seguida. O `frozen` está nas deps do efeito, então o
    // primeiro refine sai assim que a lista viva chega.
    explorerRefreshing(state);
  /**
   * A BUSCA em cena: perna + geração (`seqs[leg]`, que só um disparo de busca avança —
   * `refine_ok` de propósito não). Facetas e âncora são DELA, não da perna: uma busca
   * nova na mesma perna deixava as facetas do conjunto anterior mandando no painel e
   * no `serverCuts` — e aí o primeiro refine da lista nova saía com o teto de preço do
   * conjunto VELHO, um corte que ninguém deu. Comparar aqui (e não zerar num efeito)
   * é o que impede a âncora morta de ser usada no render em que a busca começa.
   */
  const search = [leg, state.seqs[leg] ?? 0].join(':');
  const [seen, setSeen] = useState<{ search: string; facets: FlightSearchFacets } | undefined>();
  /** A faixa de preço da BUSCA, memorizada fora do `seen` — ver `widenPriceRange`. */
  const searchRange = useRef<{ search: string; range?: { min: number; max: number } }>({
    search: '',
  });
  const facets = seen?.search === search ? seen.facets : undefined;
  const anchor = useSearchAnchor(offers, search);
  const pageCeiling = priceBounds(offers)[1];
  const cuts = serverCuts(filter, facets?.priceRange?.max ?? pageCeiling);
  /**
   * A ordenação viaja com o corte, e por o mesmo motivo dele: ordenar no cliente
   * ordena a PÁGINA, e a página é um subconjunto — "mais barato" sobre 100 de 544
   * não é o mais barato da busca. Trocar de critério é uma re-busca, não um sort
   * local.
   */
  const order = serverOrder(filter.sort);
  // A chave é o VALOR do pedido: o objeto é novo a cada render e a comparação de
  // referência dispararia uma busca por render.
  const key = JSON.stringify({ cuts, order });
  const refine = fs?.refine;
  const latest = useRef(0);
  /** Página que o scroll pediu — ver `pageWantedFor`. */
  const [wantedPage, setWantedPage] = useState<WantedPage | undefined>();
  // Stable while the page asked for holds; a retry of the SAME page (`tick`) is a new ask.
  const pageWanted = useKeyedValue(pageWantedFor(wantedPage, search, key));
  /** A rodada que POUSOU por último sem append: a busca e o corte a que a lista
   *  em cena — e o `page` dela — pertencem. */
  const [landed, setLanded] = useState<{ search: string; key: string } | undefined>();
  const legPage = legPageOf(state.legs[leg]);
  const loadMore = () => {
    // A página seguinte só pode ser pedida sobre a rodada que está EM CENA. Com
    // um refine de página 1 em voo (corte novo, debounce + ida e volta) o `page`
    // da perna ainda é o do corte ANTERIOR: pedir `number + 1` amarrado ao corte
    // NOVO tomaria a posse da rodada em voo (o efeito re-roda, `latest` sobe) e
    // pousaria a página N+1 do corte novo CONCATENADA na lista velha — lista
    // misturada e o filtro sumindo sem aviso.
    if (landed?.search !== search || landed.key !== key) return;
    if (!legPage?.hasNext) return;
    setWantedPage((w) => ({
      search,
      key,
      number: legPage.number + 1,
      tick: (w?.tick ?? 0) + 1,
    }));
  };
  /** A página filtrada NÃO é resultado de busca: `refine_ok` existe porque a perna
   *  segue `ready` e o corte que a pediu não pode ser redefinido junto. `append`
   *  marca a rodada >1 do scroll — concatena em vez de substituir. */
  const onPage = (
    offers: FlightExplorerOffer[],
    page: FlightPageInfo | undefined,
    append: boolean,
    ceiling: number | undefined,
  ) => {
    send({
      t: 'refine_ok',
      leg,
      offers,
      ...(page ? { page } : {}),
      ...(append ? { append: true } : {}),
      ...(ceiling !== undefined ? { ceiling } : {}),
    });
  };
  /** Página seguinte pedida — liga o spinner de rodapé da perna. */
  const onPageStarted = () => {
    send({ t: 'page_started', leg });
  };
  /** Página seguinte falhou — desliga o spinner, a lista fica como estava. */
  const onPageFailed = () => {
    send({ t: 'page_failed', leg });
  };
  const onPriceCeiling = (max: number) => {
    send({ t: 'filter_changed', filter: { ...filter, maxPrice: max } });
  };
  // O estado corrente para os callbacks assíncronos: o efeito só re-roda quando os
  // CORTES mudam, e a captura por closure decidiria com um teto velho. A âncora
  // mora aqui pelo motivo INVERSO dos callbacks: o disparo lê a mais fresca sem
  // que o VALOR dela re-arme o efeito (ver `hasAnchor`).
  const snapshot = {
    filter,
    pageCeiling,
    anchor,
    onPage,
    onPriceCeiling,
    onPageStarted,
    onPageFailed,
  };
  const live = useRef(snapshot);
  useLayoutEffect(() => {
    live.current = snapshot;
  });
  /**
   * A âncora entra nas deps como PRESENÇA, não como valor: morta→viva é o que
   * re-arma o efeito (o primeiro refine da busca nova sai quando a primeira página
   * chega — `key` e `search` podem ficar idênticos nesse pouso). O VALOR fica de
   * fora porque ele é só o handle da sessão (qualquer ref dela serve, ver
   * `FlightSearchFn.refine`): com ele nas deps, toda resposta de refine que
   * trocava a CABEÇA da página re-ancorava e re-agendava um refine com os mesmos
   * cortes — um request duplicado atrás de cada corte efetivo.
   */
  const hasAnchor = anchor !== undefined;
  const [busySeq, setBusySeq] = useBusySeq(refine !== undefined, hasAnchor, frozen);
  const refining = busySeq !== 0;

  useEffect(() => {
    // O seq sobe ANTES de qualquer saída: mudou dep, o que está em voo está stale.
    // Vale principalmente pra busca nova — ela não agenda refine nenhum (a âncora
    // morreu com a busca), e sem subir aqui a resposta em voo pousaria na lista nova.
    const seq = latest.current + 1;
    latest.current = seq;
    if (!refine || !hasAnchor || frozen) return undefined;
    const releaseBusy = (): void => {
      setBusySeq((s) => (s === seq ? 0 : s));
    };
    const timer = setTimeout(() => {
      // A âncora do DISPARO, não a do agendamento: entre os dois um refine pode
      // ter trocado a cabeça da página. Morrer de vez ela não morre aqui — âncora
      // só morre com busca nova, e a troca de `search` re-roda o efeito e limpa
      // este timer antes do macrotask; o guarda é o narrowing do TypeScript.
      const anchorNow = live.current.anchor;
      if (anchorNow === undefined) return;
      const pageNumber = pageWanted.number;
      const append = pageNumber > 1;
      // A rodada de página >1 NÃO acende o busySeq: `refining` inertiza a lista
      // inteira, e carregar mais não pode congelar o que já está em cena.
      if (append) live.current.onPageStarted();
      else setBusySeq(seq);
      void refine(anchorNow, {
        ...(JSON.parse(key) as { cuts: FlightServerCuts; order: FlightServerOrder }),
        ...(append ? { page: { number: pageNumber, limit: EXPLORER_PAGE_LIMIT } } : {}),
      }).then(
        (r) => {
          // Resposta stale (o dedo já mudou de eixo, ou a busca já é outra) não pode
          // pisar na página nova.
          // A rodada se apaga mesmo quando é stale — é o guarda de POSSE, fora do
          // guarda de escrita abaixo.
          releaseBusy();
          // Stale: se era append, o `loadingMore` fica aceso até o próximo pouso
          // (`refine_ok`/`search_ok` recriam a perna) — janela cosmética aceita.
          if (latest.current !== seq) return;
          const { learned, ...range } = widenPriceRange(searchRange, search, r.facets);
          const merged = { ...r.facets, ...range };
          setSeen({ search, facets: merged });
          // A lista em cena passa a ser DESTA rodada — é o que autoriza o scroll a
          // pedir a página seguinte (ver `loadMore`). Só o pouso de página 1
          // marca: o append não troca a rodada, ele a estende.
          if (!append) setLanded({ search, key });
          live.current.onPage(r.offers, r.page, append, merged.priceRange?.max);
          // O teto do slider passa a ser o do CONJUNTO. Sem rebasear, o `maxPrice`
          // herdado da primeira página (o maior preço de 30 ofertas) viraria um
          // corte de preço que ninguém deu — e capava a busca nele.
          //
          // Só na PRIMEIRA faixa da busca (`learned`), e nunca num append: a página
          // >1 não é uma faixa nova de conjunto, é mais lista da MESMA busca — rebasear
          // aqui pisaria num corte de preço que a pessoa já tinha dado antes de rolar.
          // O guarda de "intocado" é `maxPrice >= pageCeiling`, e o teto da página é 0
          // quando o corte a esvazia: dali em diante ele dá verdadeiro para qualquer
          // valor e o rebase passava por cima do corte que a pessoa acabou de fazer —
          // a lista voltava sozinha e o filtro sumia sem aviso.
          const serverMax = merged.priceRange?.max;
          const { filter: now, pageCeiling: ceil } = live.current;
          if (
            !append &&
            learned &&
            serverMax !== undefined &&
            now.maxPrice >= ceil &&
            now.maxPrice < serverMax
          ) {
            live.current.onPriceCeiling(serverMax);
          }
        },
        () => {
          // Refine que falha deixa a página e as facetas anteriores em cena: o
          // painel volta a cortar no cliente em vez de esvaziar a lista. Append que
          // falha, com posse, avisa a sentinela pra oferecer retry.
          releaseBusy();
          if (latest.current === seq && append) live.current.onPageFailed();
        },
      );
    }, 250);
    return () => {
      clearTimeout(timer);
    };
    // Deps são o VALOR dos cortes (`key`), a PRESENÇA da âncora (`hasAnchor` — o
    // valor mora no `live`, ver a nota dele), a BUSCA, o seam, o congelamento
    // e a página pedida (`pageWanted`, com o `tick` pro retry da MESMA página
    // re-disparar) — os callbacks moram no ref de propósito: são recriados a cada
    // render do explorer e entrariam em laço.
  }, [key, hasAnchor, search, refine, frozen, pageWanted, setBusySeq]);

  // O teto do CONJUNTO é fato DESTA busca, e sai daqui pronto: derivá-lo no corpo do
  // explorer só espalharia a mesma cadeia opcional por lá.
  return { facets, refining, searchCeiling: facets?.priceRange?.max, loadMore, anchor };
}
